import React, { useCallback, useEffect, useState } from 'react';
import { Plus, X, Trash2, Loader2, FileText, Eye, Upload, FileImage } from 'lucide-react';
import { apiClient, API } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const api = {
  list: () => apiClient.get('/hr/letters/templates').then((r) => r.data),
  get: (id) => apiClient.get(`/hr/letters/templates/${id}`).then((r) => r.data),
  create: (p) => apiClient.post('/hr/letters/templates', p).then((r) => r.data),
  patch: (id, p) => apiClient.patch(`/hr/letters/templates/${id}`, p).then((r) => r.data),
  del: (id) => apiClient.delete(`/hr/letters/templates/${id}`).then((r) => r.data),
  uploadLetterhead: (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    return apiClient.post(`/hr/letters/templates/${id}/letterhead`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  deleteLetterhead: (id) => apiClient.delete(`/hr/letters/templates/${id}/letterhead`).then((r) => r.data),
};

const PLACEHOLDERS = ['name', 'first_name', 'email', 'phone', 'job_title', 'department', 'country', 'salary', 'currency', 'joining_date', 'today', 'employer_name'];
const DEFAULT_BODY = `Dear {{first_name}},

We are delighted to formally offer you the position of {{job_title}} at {{employer_name}}. Following the interview process, the selection committee is pleased to extend this appointment.

Key terms:
• Designation: {{job_title}}
• Country: {{country}}
• Annual remuneration: {{salary}}
• Proposed date of joining: {{joining_date}}

This offer is subject to background verification and completion of all pre-employment formalities. Please countersign and return this letter within seven (7) days to confirm acceptance.

We look forward to welcoming you to the team.

Warm regards,
Human Resources
{{employer_name}}`;

const LetterTemplatesPage = () => {
  const [items, setItems] = useState([]);
  const [showEdit, setShowEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => { setLoading(true); try { const r = await api.list(); setItems(r.data || []); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-4 sm:mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Letter Templates</span>
          <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Appointment · Rejection · Offer</h1>
          <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Merge placeholders like <code className="bg-slate-100 px-1">{'{{name}}'}</code> at print time. An active template overrides the built-in default.</p>
        </div>
        <button onClick={() => setShowEdit({ kind: 'appointment', name: 'Standard Appointment', subject: 'Letter of Appointment · {{job_title}}', body: DEFAULT_BODY, employer_name: 'Emerald Corporates', active: true })} data-testid="new-tpl-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#0f5c3d] text-white text-xs"><Plus className="w-3.5 h-3.5" /> New template</button>
      </div>
      {loading ? <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#0f5c3d] inline" /></div> : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8]"><tr>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Kind</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Name</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Employer</th>
              <th className="text-left p-2 text-xs uppercase text-[#0b1a2e]/60">Status</th>
              <th></th>
            </tr></thead>
            <tbody>{items.map((t) => (
              <tr key={t.id} className="border-t border-black/5 hover:bg-[#f7fbf8]" data-testid={`tpl-row-${t.id}`}>
                <td className="p-2"><span className="text-[10px] uppercase font-bold bg-[#0f5c3d]/10 text-[#0f5c3d] px-1.5 py-0.5 rounded">{t.kind}</span></td>
                <td className="p-2">{t.name}</td>
                <td className="p-2 text-xs">{t.employer_name || '—'}</td>
                <td className="p-2 text-xs">{t.active ? <span className="text-emerald-700 font-bold">Active</span> : 'Inactive'}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button onClick={() => window.open(`${API}/hr/letters/templates/${t.id}/render`, '_blank')} className="text-xs text-[#0f5c3d] mr-2 inline-flex items-center gap-0.5"><Eye className="w-3 h-3" /> Preview</button>
                  <button onClick={() => setShowEdit(t)} data-testid={`edit-tpl-${t.id}`} className="text-xs text-[#0f5c3d] mr-2">Edit</button>
                  <button onClick={async () => { if (window.confirm(`Delete "${t.name}"?`)) { await api.del(t.id); load(); } }} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}</tbody>
          </table>
          {items.length === 0 && <div className="p-8 text-center text-sm text-[#0b1a2e]/50">No templates yet.</div>}
        </div>
      )}
      {showEdit && <EditModal initial={showEdit} onClose={() => setShowEdit(null)} onSaved={() => { setShowEdit(null); load(); }} />}
    </div>
  );
};

const EditModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial);
  const isEdit = !!f.id;
  const [saving, setSaving] = useState(false);
  const [uploadingLh, setUploadingLh] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) await api.patch(f.id, f); else {
        const created = await api.create(f);
        setF({ ...f, id: created.id });
      }
      toast({ title: 'Saved' }); onSaved();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  const uploadLh = async (file) => {
    if (!f.id) { toast({ title: 'Save the template first, then upload letterhead', variant: 'destructive' }); return; }
    setUploadingLh(true);
    try {
      const r = await api.uploadLetterhead(f.id, file);
      toast({ title: 'Letterhead uploaded', description: `${(r.size / 1024).toFixed(0)} KB` });
      const t = await api.get(f.id); setF(t);
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Upload failed', variant: 'destructive' }); }
    finally { setUploadingLh(false); }
  };
  const removeLh = async () => {
    if (!f.id || !window.confirm('Remove letterhead? Letters will fall back to plain layout.')) return;
    try { await api.deleteLetterhead(f.id); const t = await api.get(f.id); setF(t); toast({ title: 'Letterhead removed' }); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };
  const insertToken = (tok) => setF({ ...f, body: (f.body || '') + `{{${tok}}}` });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 bg-[#0b1a2e] text-white flex justify-between"><div className="font-medium">{isEdit ? 'Edit' : 'New'} letter template</div><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Kind</label>
              <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="tpl-kind" className="w-full px-3 py-2 border rounded text-sm bg-white">
                {['appointment', 'rejection', 'offer', 'termination', 'relieving', 'experience'].map((k) => <option key={k}>{k}</option>)}
              </select></div>
            <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Template name</label>
              <input value={f.name || ''} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="tpl-name" className="w-full px-3 py-2 border rounded text-sm" /></div>
          </div>
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Employer name</label>
            <input value={f.employer_name || ''} onChange={(e) => setF({ ...f, employer_name: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" /></div>
          <div><label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Subject line</label>
            <input value={f.subject || ''} onChange={(e) => setF({ ...f, subject: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" placeholder="e.g. Letter of Appointment · {{job_title}}" /></div>

          {/* Letterhead PDF upload */}
          <div className="bg-[#f7fbf8] rounded-lg p-3 border border-[#0f5c3d]/10">
            <div className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60 mb-2 flex items-center gap-1"><FileImage className="w-3 h-3" /> Letterhead PDF</div>
            {f.has_letterhead ? (
              <div className="flex items-center justify-between">
                <span className="text-xs text-emerald-700">✓ Letterhead uploaded — merged text will overlay page 1</span>
                <button onClick={removeLh} data-testid="lh-remove" className="text-xs text-red-600 hover:underline">Remove</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input type="file" accept=".pdf" onChange={(e) => e.target.files?.[0] && uploadLh(e.target.files[0])} data-testid="lh-file" className="text-xs flex-1" />
                {uploadingLh && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              </div>
            )}
            <p className="text-[10px] text-[#0b1a2e]/50 mt-1">Upload your designed A4 PDF stationery; body text is overlaid on page 1. Save the template before uploading.</p>
            {f.has_letterhead && (
              <div className="grid grid-cols-4 gap-2 mt-3">
                {[['overlay_top_margin', 'Top'], ['overlay_bottom_margin', 'Bottom'], ['overlay_left_margin', 'Left'], ['overlay_right_margin', 'Right']].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-[9px] uppercase font-semibold text-[#0b1a2e]/60">{l} (pt)</label>
                    <input type="number" value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1 border rounded text-xs" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase font-semibold text-[#0b1a2e]/60">Body</label>
              <div className="text-[10px] flex gap-1 flex-wrap">
                {PLACEHOLDERS.map((p) => (
                  <button key={p} onClick={() => insertToken(p)} className="px-1.5 py-0.5 rounded bg-[#f7fbf8] border border-[#0f5c3d]/20 hover:bg-[#0f5c3d]/10">{`{{${p}}}`}</button>
                ))}
              </div>
            </div>
            <textarea value={f.body || ''} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="tpl-body" rows={14} className="w-full px-3 py-2 border rounded text-sm font-mono" />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active (used automatically for this kind of letter)</label>
          <div className="flex gap-2 mt-2">
            <button onClick={save} disabled={saving} data-testid="tpl-save-btn" className="flex-1 px-4 py-2 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">{saving && <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />}Save</button>
            {f.id && <button onClick={() => window.open(`${API}/hr/letters/templates/${f.id}/render`, '_blank')} className="px-4 py-2 rounded-full border border-[#0f5c3d]/20 text-sm inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Preview</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LetterTemplatesPage;
